import {
  formatDate,
  formatShortDate,
  formatTime,
  toDateKey,
  type MailerAddress,
  type MailerComposeMode,
  type MailerMessage,
} from '@nesc/shared';

/** 一覧の日付。今日は時刻、今年は月日、それ以前は年月日（Gmail と同じ） */
export const listDate = (iso: string, now: Date = new Date()): string => {
  if (toDateKey(iso) === toDateKey(now)) return formatTime(iso);
  if (toDateKey(iso).slice(0, 4) === toDateKey(now).slice(0, 4)) return formatShortDate(iso);
  return formatDate(iso);
};

/** 件名に Re: / Fwd: を付ける。すでに付いていれば重ねない */
export const prefixSubject = (subject: string, mode: MailerComposeMode): string => {
  const base = subject.trim();
  if (mode === 'reply' || mode === 'replyAll') return /^re:/i.test(base) ? base : `Re: ${base}`;
  if (mode === 'forward') return /^(fwd?|転送):/i.test(base) ? base : `Fwd: ${base}`;
  return base;
};

const uniq = (list: string[]): string[] => [...new Set(list.map((e) => e.toLowerCase()))];

/**
 * 返信の宛先。自分が送ったメールへの返信は元の宛先へ、相手からのメールは Reply-To（無ければ差出人）へ。
 * 全員に返信では元の To・Cc から自分と To に入れた相手を除いて Cc に入れる。
 */
export const replyRecipients = (
  message: Pick<MailerMessage, 'from' | 'to' | 'cc' | 'replyTo' | 'fromMe'>,
  mode: MailerComposeMode,
  me: string,
): { to: string[]; cc: string[] } => {
  if (mode === 'new' || mode === 'forward') return { to: [], cc: [] };
  const self = me.toLowerCase();
  const emails = (list: MailerAddress[]) => list.map((a) => a.email);

  const to = message.fromMe
    ? uniq(emails(message.to))
    : uniq(message.replyTo.length ? emails(message.replyTo) : message.from ? [message.from.email] : []);
  if (mode === 'reply') return { to, cc: [] };

  const cc = uniq([...emails(message.to), ...emails(message.cc)]).filter((e) => e !== self && !to.includes(e));
  return { to: to.length ? to : cc.splice(0, 1), cc };
};

/** 本文末尾に署名を入れる（Gmail の「--」区切り） */
export const withSignature = (body: string, signature: string): string =>
  signature ? `${body}\n\n--\n${signature}` : body;

/** 本文から署名（最後の「--」行以降）を外す。AI には署名を除いた書きかけだけを渡す */
export const withoutSignature = (body: string): string => {
  const lines = body.replace(/\r\n/g, '\n').split('\n');
  const index = lines.lastIndexOf('--');
  return (index >= 0 ? lines.slice(0, index) : lines).join('\n').trim();
};

export const formatBytes = (size: number): string => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

export const addressLabel = (address: MailerAddress | null, me?: string): string => {
  if (!address) return '（不明）';
  if (me && address.email === me.toLowerCase()) return '自分';
  return address.name || address.email;
};
